import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Save, Award, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { ProgressDialog } from "@/components/ProgressDialog";
import { ShowcaseUploadDialog, TeacherUploads } from "@/components/ShowcaseUpload";
import { DoubtInbox } from "@/components/DoubtInbox";
import { TrainerClassReport } from "@/components/ClassHistory";
import { TeacherSmartPanel } from "@/components/smart/TeacherSmartPanel";
import { TeacherRooms } from "@/components/breakout/TeacherRooms";
import { api } from "@/lib/api";

const today = () => format(new Date(), "yyyy-MM-dd");

export default function TeacherDashboard() {
  const nav = useNavigate();
  const [teacher, setTeacher] = useState(null);
  const [students, setStudents] = useState([]);
  const [date, setDate] = useState(today());
  const [marks, setMarks] = useState({});
  const [saving, setSaving] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [uploads, setUploads] = useState([]);
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${localStorage.getItem("rs_teacher_token")}` } }), []);

  const load = useCallback(() => {
    api.get("/teacher/students", auth).then((r) => { setTeacher(r.data.teacher); setStudents(r.data.items); })
      .catch((e) => { if (e.response?.status === 401) { localStorage.removeItem("rs_teacher_token"); nav("/teacher/login"); } });
    api.get("/teacher/showcase", auth).then((r) => setUploads(r.data.items)).catch(() => {});
  }, [auth, nav]);

  useEffect(() => {
    if (!localStorage.getItem("rs_teacher_token")) { nav("/teacher/login"); return; }
    load();
  }, [load, nav]);

  // Pre-fill register from saved attendance for the chosen date (default present).
  useEffect(() => {
    const m = {};
    students.forEach((s) => {
      const rec = (s.progress?.attendance || []).find((a) => a.date === date);
      m[s.id] = rec ? rec.present : true;
    });
    setMarks(m);
  }, [students, date]);

  const saveRegister = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/teacher/attendance", { date, records: students.map((s) => ({ enrollment_id: s.id, present: marks[s.id] })) }, auth);
      toast.success(`Attendance saved for ${data.saved} students`);
      load();
    } catch { toast.error("Could not save attendance"); } finally { setSaving(false); }
  };

  const logout = () => { localStorage.removeItem("rs_teacher_token"); nav("/teacher/login"); };
  const presentCount = Object.values(marks).filter(Boolean).length;

  return (
    <div className="bg-[#E8F0FE] min-h-screen" data-testid="teacher-dashboard">
      <div className="max-w-5xl mx-auto px-5 py-10">
        <div className="flex justify-between items-start flex-wrap gap-3">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF2E63]">Trainer</span>
            <h1 className="text-3xl font-extrabold text-[#0A192F]" data-testid="teacher-name">{teacher?.name || "…"}</h1>
            <p className="text-slate-600 text-sm" data-testid="teacher-school">{teacher?.school_name}</p>
          </div>
          <Button variant="outline" onClick={logout} data-testid="teacher-logout-btn"><LogOut className="w-4 h-4 mr-1" />Logout</Button>
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200">
          <div className="p-5 border-b border-slate-200 flex flex-wrap gap-3 items-center justify-between">
            <div>
              <h2 className="font-bold text-lg text-[#0A192F]">Class register</h2>
              <p className="text-xs text-slate-500">{presentCount} of {students.length} present · tap a name to toggle</p>
            </div>
            <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto">
              <Popover open={pickOpen} onOpenChange={setPickOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-10 flex-1 sm:flex-none sm:w-44 justify-start font-normal" data-testid="register-date">
                    <CalendarDays className="w-4 h-4 mr-2 text-[#FF2E63]" />{format(new Date(`${date}T00:00:00`), "dd MMM yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end" data-testid="register-date-picker">
                  <Calendar mode="single" selected={new Date(`${date}T00:00:00`)} disabled={{ after: new Date() }} initialFocus
                    onSelect={(d) => { if (d) { setDate(format(d, "yyyy-MM-dd")); setPickOpen(false); } }} />
                </PopoverContent>
              </Popover>
              <Button onClick={saveRegister} disabled={saving || !students.length} className="rs-cta rounded-full h-10 flex-1 sm:flex-none" data-testid="register-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1" />Save attendance</>}
              </Button>
            </div>
          </div>
          {students.length === 0 ? (
            <p className="p-8 text-center text-slate-500" data-testid="teacher-no-students">No paid enrollments for your school yet.</p>
          ) : (
            <ul className="divide-y" data-testid="register-list">
              {students.map((s) => {
                const p = s.progress || {};
                const att = p.attendance || [];
                const pct = att.length ? Math.round((att.filter((a) => a.present).length / att.length) * 100) : null;
                return (
                  <li key={s.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`student-row-${s.id}`}>
                    <button type="button" onClick={() => setMarks({ ...marks, [s.id]: !marks[s.id] })} data-testid={`register-toggle-${s.id}`}
                      className={`w-24 h-10 rounded-lg text-xs font-bold shrink-0 transition-colors ${marks[s.id] ? "bg-emerald-500/15 text-emerald-700" : "bg-red-500/15 text-red-600"}`}>
                      {marks[s.id] ? "Present" : "Absent"}
                    </button>
                    <div className="flex-1 min-w-[160px]">
                      <div className="font-semibold text-[#0A192F] flex items-center gap-2">{s.student_name}{p.program_completed && <Award className="w-4 h-4 text-[#FF2E63]" />}</div>
                      <div className="text-xs text-slate-500">Class {s.class_grade} · {s.program} · {pct === null ? "no sessions yet" : `${pct}% attendance`} · {(p.projects || []).filter((x) => x.status === "completed").length} projects done</div>
                    </div>
                    <ShowcaseUploadDialog student={s} auth={auth} onDone={load} />
                    <ProgressDialog key={p.updated_at || s.id} enrollment={s} auth={auth} onDone={load} endpoint="teacher" />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <TeacherSmartPanel auth={auth} />
        <TeacherRooms auth={auth} />
        <TrainerClassReport auth={auth} />
        <DoubtInbox auth={auth} />
        <TeacherUploads items={uploads} token={localStorage.getItem("rs_teacher_token")} />
      </div>
    </div>
  );
}
